import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import api from "@/lib/api";
import HeroMapBackground from "@/components/HeroMapBackground";
import { US_STATES, CODE_TO_NAME } from "@/lib/states";
import { useAuth } from "@/context/AuthContext";
import { detectUsState } from "@/lib/geo";

/* -------- Masked line reveal helper -------- */
const RevealLine = ({ children, delay = 0, className = "" }) => (
    <span className="mask-line">
        <motion.span
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
            className={`inline-block ${className}`}
        >
            {children}
        </motion.span>
    </span>
);

/* -------- Section wrapper with scroll-in reveal -------- */
const Section = ({ children, className = "", ...rest }) => (
    <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className={className}
        {...rest}
    >
        {children}
    </motion.section>
);

export default function Landing() {
    const { user } = useAuth();
    const [stats, setStats] = useState({ players: 0, teams: 0, tournaments: 0, prize_pool: 0 });
    const [featured, setFeatured] = useState([]);
    const [players, setPlayers] = useState([]);
    const [homeStateName, setHomeStateName] = useState(null);
    const heroRef = useRef(null);
    const videoRef = useRef(null);
    const [videoMuted, setVideoMuted] = useState(true);

    const toggleAudio = () => {
        const v = videoRef.current;
        if (!v) return;
        const next = !videoMuted;
        v.muted = next;
        setVideoMuted(next);
        // Ensure playback resumes on user gesture (mobile Safari, etc.)
        if (!next) {
            v.currentTime = 0;
            v.play?.().catch(() => {});
        }
    };

    useEffect(() => {
        api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/tournaments/featured").then((r) => setFeatured(r.data)).catch(() => {});
        api.get("/leaderboard/players", { params: { limit: 6 } }).then((r) => setPlayers(r.data)).catch(() => {});
    }, []);

    // Highlight the viewer's home state on the hero map (account state, else IP geo)
    useEffect(() => {
        if (user?.state) { setHomeStateName(CODE_TO_NAME[user.state] || null); return; }
        let alive = true;
        detectUsState().then(c => { if (alive && c) setHomeStateName(CODE_TO_NAME[c] || null); });
        return () => { alive = false; };
    }, [user]);

    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const heroY = useTransform(scrollYProgress, [0, 1], [0, 160]);
    const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

    return (
        <div className="relative">
            {/* ============ HERO ============ */}
            <section ref={heroRef} className="relative min-h-[100vh] pt-[68px] overflow-hidden" data-testid="hero-section">
                {/* Parallax map layer */}
                <motion.div style={{ y: heroY, scale: heroScale, opacity: heroOpacity }} className="absolute inset-0 z-0 pointer-events-none">
                    <HeroMapBackground highlight={homeStateName} />
                    <div className="absolute inset-0 bg-gradient-to-b from-void/10 via-void/50 to-void pointer-events-none" />
                    <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none" />
                </motion.div>

                <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-28 md:pt-36">
                    {/* Top strip */}
                    <div className="flex items-start justify-between mb-10">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }}>
                            <div className="overline">[ 01 ] The Pro Circuit</div>
                            <div className="mt-3 font-mono text-xs text-white/50">SEASON 0 · GLOBAL BROADCAST</div>
                        </motion.div>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 1 }} className="text-right">
                            <div className="overline text-white/50">Live now</div>
                            <div className="mt-3 flex items-center gap-2 justify-end">
                                <span className="w-2 h-2 rounded-full bg-signal live-dot" />
                                <span className="font-mono text-xs text-white/80">RESURGENCE · TEST</span>
                            </div>
                        </motion.div>
                    </div>

                    <div className="grid md:grid-cols-12 gap-8 items-start">
                        <div className="md:col-span-8">
                            {/* Kinetic headline */}
                            <h1 className="font-display font-black tracking-tighter leading-[0.86] text-[13vw] md:text-[8vw] lg:text-[7.5vw]" data-testid="hero-headline">
                                <RevealLine delay={0.35}>NOT A</RevealLine>
                                <br />
                                <RevealLine delay={0.5} className="text-volt">TOURNAMENT.</RevealLine>
                                <br />
                                <RevealLine delay={0.65}>A <span className="text-outline-strong">VERDICT</span>.</RevealLine>
                            </h1>
                        </div>

                        {/* Broadcast panel (Sora hero video) */}
                        <motion.div
                            initial={{ opacity: 0, x: 30, scale: 0.95 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            transition={{ delay: 0.7, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="md:col-span-4 relative"
                            data-testid="hero-broadcast-panel"
                        >
                            <div className="relative border border-white/15 clip-frame-sm bg-[#050505] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5),0_0_40px_rgba(204,255,0,0.08)] aspect-[9/16] max-w-[280px] mx-auto md:mx-0 md:ml-auto">
                                <video
                                    ref={videoRef}
                                    src="/state-champion-hero.mp4"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    poster=""
                                    className="w-full h-full object-cover"
                                    data-testid="hero-broadcast-video"
                                />
                                <div className="absolute inset-x-0 top-0 p-3 flex items-center justify-between">
                                    <div className="flex items-center gap-1.5 border border-signal/70 bg-black/40 backdrop-blur-sm px-2 py-1 font-mono text-[9px] tracking-[0.24em] uppercase text-signal pointer-events-none">
                                        <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />
                                        LIVE FEED
                                    </div>
                                    <button
                                        type="button"
                                        onClick={toggleAudio}
                                        aria-label={videoMuted ? "Unmute video" : "Mute video"}
                                        aria-pressed={!videoMuted}
                                        data-testid="hero-audio-toggle"
                                        className={`inline-flex items-center gap-1.5 border px-2 py-1 font-mono text-[9px] tracking-[0.24em] uppercase transition-colors backdrop-blur-sm ${
                                            videoMuted
                                                ? "border-white/30 bg-black/50 text-white/80 hover:border-volt/60 hover:text-volt"
                                                : "border-volt/70 bg-volt/[0.12] text-volt hover:bg-volt/[0.2]"
                                        }`}
                                    >
                                        <span aria-hidden="true">{videoMuted ? "🔇" : "🔊"}</span>
                                        <span>{videoMuted ? "Tap for sound" : "Sound on"}</span>
                                    </button>
                                </div>
                                <div className="absolute inset-x-0 bottom-0 p-3 pointer-events-none bg-gradient-to-t from-black/85 to-transparent">
                                    <div className="overline text-volt mb-1">[ Announcing ]</div>
                                    <div className="font-display font-black text-white text-sm leading-tight">
                                        State Champion Series.
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    <div className="mt-14 grid md:grid-cols-12 gap-8 items-end">
                        <div className="md:col-span-5">
                            <RevealLine delay={0.95}>
                                <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-md">
                                    Ranked Play is the Best Underground circuit for the top 1%. We are looking for the best players in each state to compete for their home!
                                </p>
                            </RevealLine>
                        </div>
                        <div className="md:col-span-4 md:col-start-9 flex flex-wrap gap-3">
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}>
                                <Link to="/tournaments" className="btn-primary" data-testid="hero-cta-tournaments">Browse Tournaments →</Link>
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}>
                                <Link to="/register" className="btn-ghost" data-testid="hero-cta-register">Register a team</Link>
                            </motion.div>
                        </div>
                    </div>

                    {/* Stats strip */}
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.9 }}
                        className="mt-24 grid grid-cols-2 md:grid-cols-4 border-t border-white/10">
                        {[
                            ["Players", stats.players],
                            ["Teams", stats.teams],
                            ["Tournaments", stats.tournaments],
                            ["Prize pool", `$${(stats.prize_pool || 0).toLocaleString()}`],
                        ].map(([label, val], i) => (
                            <div key={label} className={`py-8 md:py-10 ${i > 0 ? "md:border-l border-white/10" : ""} ${i > 1 ? "border-t md:border-t-0 border-white/10" : ""}`}>
                                <div className="overline text-white/40">{label}</div>
                                <div className="mt-2 font-display font-black tracking-tighter text-3xl md:text-5xl">{val}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* ============ STATES STRIP ============ */}
            <div className="border-y border-white/5 bg-void/60 backdrop-blur-sm" data-testid="states-strip">
                <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-6 flex items-center gap-8">
                    <div className="overline text-white/40 whitespace-nowrap hidden md:block">[ States ]</div>
                    <div className="h-4 w-px bg-white/10 hidden md:block" />
                    <div className="flex-1 overflow-hidden">
                        <Marquee gradient={false} speed={28} pauseOnHover>
                            {US_STATES.map(([code, name]) => (
                                <div key={code} className="flex items-center gap-3 pr-10 group cursor-default" data-testid={`state-${code.toLowerCase()}`}>
                                    <span className="w-1.5 h-1.5 rounded-full bg-volt/60 group-hover:bg-volt transition-colors" />
                                    <span className="font-mono text-xs tracking-[0.28em] text-white/45 group-hover:text-white/85 transition-colors uppercase">{name}</span>
                                </div>
                            ))}
                        </Marquee>
                    </div>
                    <div className="overline text-white/30 hidden lg:block whitespace-nowrap">50 states · All live</div>
                </div>
            </div>

            {/* ============ EDITORIAL MARQUEE ============ */}
            <div className="border-y border-white/5 py-8 md:py-12 bg-void">
                <Marquee gradient={false} speed={30} className="overflow-hidden">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-16 pr-16">
                            <span className="marquee-outline text-6xl md:text-8xl">RANKED PLAY</span>
                            <span className="text-volt text-5xl md:text-7xl">✦</span>
                            <span className="marquee-outline text-6xl md:text-8xl">PRO CIRCUIT</span>
                            <span className="text-volt text-5xl md:text-7xl">✦</span>
                            <span className="marquee-outline text-6xl md:text-8xl">SEASON 0</span>
                            <span className="text-volt text-5xl md:text-7xl">✦</span>
                        </div>
                    ))}
                </Marquee>
            </div>

            {/* ============ FEATURED TOURNAMENTS - BENTO ============ */}
            <Section className="max-w-[1600px] mx-auto px-6 md:px-10 section" data-testid="featured-section">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
                    <div>
                        <div className="overline mb-4">[ 02 ] Featured Circuits</div>
                        <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.9] max-w-2xl">
                            The stages worth <span className="text-outline-strong">bleeding</span> for.
                        </h2>
                    </div>
                    <Link to="/tournaments" className="font-mono text-xs tracking-[0.24em] uppercase text-white/60 hover:text-volt transition-colors">
                        View all {stats.tournaments || 0} →
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
                    {featured.slice(0, 3).map((t, i) => (
                        <FeatureCard key={t.slug || i} t={t} large={i === 0} />
                    ))}
                </div>
            </Section>

            {/* ============ MANIFESTO ============ */}
            <Section className="relative border-y border-white/5 bg-[#050505] section overflow-hidden" data-testid="manifesto-section">
                <img src="/brand/watermark.png" alt="" className="absolute -right-32 top-1/2 -translate-y-1/2 w-[720px] opacity-[0.06] pointer-events-none select-none hidden md:block" />
                <div className="max-w-[1600px] mx-auto px-6 md:px-10">
                    <div className="overline mb-4">[ 03 ] The Mission</div>
                    <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl mb-8 max-w-3xl leading-[0.9]">
                        The best player <br />in every <span className="text-volt">state</span>.
                    </h2>
                    <p className="max-w-2xl text-secondary text-base md:text-lg leading-relaxed mb-24">
                        Ranked Play Tournaments is live in all 50 states to find the best players in the country — and every bracket is <span className="text-white">locked to your home state</span>. Open qualifiers for <span className="text-white">Call of Duty: Modern Warfare 4</span> — <span className="text-white">Warzone</span> squads and <span className="text-white">4v4 Multiplayer</span> teams. The top 30 squads in every state advance to the semifinals. Each state crowns its own champions, and the champions represent their state at the national final.
                    </p>

                    <div className="space-y-16">
                        {MANIFESTO.map((m, i) => (
                            <ManifestoChapter key={m.chapter} chapter={m.chapter} title={m.title} body={m.body} even={i % 2 === 1} />
                        ))}
                    </div>

                    <div className="mt-24 pt-10 border-t border-white/10 flex flex-wrap items-center gap-6">
                        <Link to="/roadmap" className="btn-ghost" data-testid="landing-roadmap-cta">See the national roadmap →</Link>
                        <Link to="/champions" className="font-mono text-xs tracking-[0.24em] uppercase text-white/60 hover:text-volt transition-colors" data-testid="landing-champions-cta">
                            View the champion wall →
                        </Link>
                    </div>
                </div>
            </Section>

            {/* ============ SPOTLIGHT PLAYERS ============ */}
            <Section className="max-w-[1600px] mx-auto px-6 md:px-10 section" data-testid="players-section">
                <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
                    <div className="md:col-span-7">
                        <div className="overline mb-4">[ 04 ] Top of the ladder</div>
                        <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.9]">
                            Names that <br />
                            <span className="text-volt">history</span> remembers.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9">
                        <p className="text-secondary text-base leading-relaxed">
                            The Ranked Play ladder never resets. Every win compounds. Every loss stings. The top 6 right now — no filler, no favors.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {players.slice(0, 6).map((p, i) => (
                        <PlayerCard key={p.gamer_tag} p={p} rank={i + 1} />
                    ))}
                </div>
            </Section>

            {/* ============ CTA ============ */}
            <Section className="relative section overflow-hidden" data-testid="cta-section">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/7849519/pexels-photo-7849519.jpeg?auto=compress&cs=tinysrgb&w=1800')] bg-cover bg-center opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-b from-void via-void/70 to-void" />
                    <img src="/brand/watermark.png" alt="" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[85vw] max-w-[900px] opacity-[0.08] pointer-events-none select-none" />
                </div>
                <div className="max-w-[1600px] mx-auto px-6 md:px-10 text-center">
                    <div className="overline mb-6">[ 05 ] Enter the arena</div>
                    <h2 className="font-display font-black tracking-tighter text-5xl md:text-8xl leading-[0.88] max-w-4xl mx-auto">
                        Your ladder <br />starts <span className="text-volt">tonight</span>.
                    </h2>
                    <p className="mt-8 text-secondary max-w-xl mx-auto leading-relaxed">
                        Registration is open. First match placement within 48 hours of team formation.
                    </p>
                    <div className="mt-12 flex flex-wrap gap-4 justify-center">
                        <Link to="/register" className="btn-primary" data-testid="cta-register-btn">Create Account</Link>
                        <Link to="/tournaments" className="btn-ghost" data-testid="cta-browse-btn">Browse tournaments</Link>
                    </div>
                </div>
            </Section>
        </div>
    );
}

/* ---------- Featured card ---------- */
const FeatureCard = ({ t, large }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`relative group ${large ? "md:col-span-8 md:row-span-2 min-h-[560px]" : "md:col-span-4 min-h-[270px]"}`}
    >
        {(() => {
            const isLocked = (t.bracket_tier === "semifinals" || t.bracket_tier === "championship") && t.status !== "live" && t.status !== "finished";
            const CardWrap = isLocked ? "div" : Link;
            const cardProps = isLocked ? {} : { to: `/tournaments/${t.slug}` };
            return (
                <CardWrap {...cardProps} data-testid={`featured-${t.slug}`} className="block relative h-full overflow-hidden clip-frame bg-[#0A0A0A] border border-white/10 card-lift">
                    <div className={`absolute inset-0 bg-cover bg-center transition-transform duration-700 ${isLocked ? "opacity-40" : "group-hover:scale-105"}`}
                        style={{ backgroundImage: `url(${t.banner_url})` }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />

                    {isLocked && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center backdrop-blur-[2px] bg-void/55" data-testid={`featured-lock-${t.slug}`}>
                            <div className={`${large ? "w-20 h-20" : "w-14 h-14"} border border-volt/40 bg-void/70 flex items-center justify-center shadow-[0_0_28px_rgba(168,85,247,0.35)]`}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={`${large ? "w-10 h-10" : "w-7 h-7"} text-volt`}>
                                    <rect x="4" y="11" width="16" height="10" rx="1.5" />
                                    <path d="M8 11V7a4 4 0 018 0v4" />
                                </svg>
                            </div>
                            <div className={`mt-3 font-mono tracking-[0.32em] uppercase text-volt ${large ? "text-xs" : "text-[10px]"}`}>Locked</div>
                            <div className={`mt-2 font-display font-black text-white text-center px-6 leading-tight ${large ? "text-xl" : "text-sm"}`}>
                                Unlocked by qualifier results.
                            </div>
                        </div>
                    )}

                    <div className={`relative h-full p-6 md:p-8 flex flex-col justify-between ${isLocked ? "opacity-40" : ""}`}>
                        <div className="flex items-center justify-between">
                            <div className="overline">{t.game}</div>
                            {t.status === "live" && (
                                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-signal uppercase">
                                    <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" /> Live
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="font-mono text-xs text-white/50 mb-2">${t.prize_pool.toLocaleString()} · {t.format}</div>
                            <h3 className={`font-display font-black tracking-tighter leading-[0.95] ${large ? "text-4xl md:text-6xl" : "text-2xl md:text-3xl"}`}>
                                {t.name}
                            </h3>
                            <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                                <span>{t.region} · {t.start_date}</span>
                                {!isLocked && <span className="text-volt">Enter →</span>}
                            </div>
                        </div>
                    </div>
                </CardWrap>
            );
        })()}
    </motion.div>
);

/* ---------- Manifesto ---------- */
const MANIFESTO = [
    { chapter: "01", title: "Every state. Every player.", body: "We're on a mission to surface the best competitive players in every state in America. No coastal bias. No qualifier tax. If you can frag, we'll find you." },
    { chapter: "02", title: "Locked to your home state.", body: "All 50 states are live right now. Your account is tied to the state you live in — you fight for your home ladder, your home bracket, your home crown." },
    { chapter: "03", title: "Open qualifier. Ruthless bracket.", body: "Both MW4 qualifiers run open in every state — enter as many squads as you want until close. Warzone and 4v4 Multiplayer: top 30 squads in each state advance to the semis, then the finals." },
    { chapter: "04", title: "One state champion per title.", body: "Each state crowns one Warzone champion and one MW4 Multiplayer champion. Names go on the wall. Trophies go home." },
    { chapter: "05", title: "State champions go national.", body: "Every state's two champions advance to the national stage — against the winners of all forty-nine other states." },
];

const ManifestoChapter = ({ chapter, title, body, even }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`grid md:grid-cols-12 gap-6 md:gap-10 items-baseline ${even ? "md:flex-row-reverse" : ""}`}
    >
        <div className="md:col-span-2 font-mono text-volt text-sm tracking-[0.3em]">[ {chapter} ]</div>
        <div className="md:col-span-6">
            <h3 className="font-display font-extrabold tracking-tight text-2xl md:text-4xl leading-[1.05]">{title}</h3>
        </div>
        <div className="md:col-span-4">
            <p className="text-secondary text-base leading-relaxed">{body}</p>
        </div>
    </motion.div>
);

/* ---------- Player card ---------- */
const PlayerCard = ({ p, rank }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        transition={{ duration: 0.7, delay: rank * 0.05, ease: [0.16, 1, 0.3, 1] }}
        className="relative border border-white/10 clip-frame-sm p-6 bg-[#0A0A0A] card-lift"
        data-testid={`player-card-${rank}`}
    >
        <div className="flex items-start justify-between mb-6">
            <div className="font-display font-black text-6xl text-volt tracking-tighter leading-none">#{rank}</div>
            <div className="text-right">
                <div className="font-mono text-[10px] tracking-[0.24em] text-white/40 uppercase">ELO</div>
                <div className="font-display font-black text-2xl">{p.elo}</div>
            </div>
        </div>
        <div className="font-display font-extrabold text-2xl leading-tight">/{p.gamer_tag}</div>
        <div className="mt-1 text-secondary text-sm">{p.name} · {p.country}</div>
        <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between font-mono text-xs">
            <span className="text-white/60">{p.main_game || "MULTI"}</span>
            <span className="text-white/60">{p.wins}W · {p.losses}L</span>
        </div>
    </motion.div>
);
